import {
  Autocomplete,
  Box,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import Button from '@/components/Button'
import Dialog from '@/components/Dialog'
import { useAppSelector } from '@/hooks/redux'
import type { AppDispatch } from '@/store'
import {
  useGetRecipesQuery,
  useMoveCaseImageMutation,
} from '@/store/apiService'
import { clearImageSelection } from '@/store/project'

type MoveImageDialogProps = {
  open: boolean
  onClose: () => void
  refetchRecipe: () => void
  caseId: string
  imageId?: string
}

const MoveImageDialog: React.FC<MoveImageDialogProps> = ({
  open,
  onClose,
  refetchRecipe,
  caseId,
  imageId,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'ProjectDetails.MoveImageDialog',
  })
  const theme = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const selected = useAppSelector(
    (state) => state?.project?.selectedImageIds?.[caseId] || [],
  )

  const queryArgs = useMemo(() => {
    return {
      shared: false,
      paginated: false,
    }
  }, [])

  const { data: recipeData, refetch } = useGetRecipesQuery(queryArgs, {
    skip: !open,
  })

  useEffect(() => {
    if (open) {
      refetch()
    }
  }, [open, refetch])

  const [selectedProject, setSelectedProject] = useState(null)
  const [moveCaseImage, { isLoading: isMoving }] = useMoveCaseImageMutation()

  const projectOptions = useMemo(() => {
    if (!Array.isArray(recipeData)) return []

    return recipeData
      .filter((item) => item.id !== caseId)
      .map((item) => ({
        ...item,
        title: item.name || 'Untitled',
      }))
  }, [recipeData, caseId])

  const handleInvitedUserChange = (
    _event: React.SyntheticEvent,
    newValue: any,
  ) => {
    if (newValue?.disabled) return
    setSelectedProject(newValue)
  }

  const handleAddUser = async () => {
    if (selectedProject?.id) {
      try {
        const ids = imageId !== undefined ? [imageId] : [selected]

        await moveCaseImage({
          ids,
          caseId: caseId,
          data: {
            case_id: selectedProject?.id,
          },
        }).unwrap()
        toast.success(t('imageMoved'))
        dispatch(clearImageSelection(caseId))
        setSelectedProject(null)
        onClose()
        refetchRecipe()
      } catch (err) {
        toast.error(t('imageMoveFailed'))
      }
    }
  }
  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      title={
        <Typography fontSize={20} fontWeight={500}>
          {t('title')}
        </Typography>
      }
      content={
        <Box display="flex" flexDirection="column" gap={3}>
          <Box display={'flex'} flexDirection="column" gap={2}>
            <Autocomplete
              value={selectedProject}
              onChange={handleInvitedUserChange}
              options={projectOptions}
              getOptionLabel={(option) => {
                if (typeof option === 'string') {
                  return option
                }
                if (option.inputValue) {
                  return option.inputValue
                }
                return option.title
              }}
              renderOption={(props, option) => (
                <li {...props} key={`${option.id}-${option.title}`}>
                  <Box>
                    <Typography fontWeight={400} fontSize={'16px'}>
                      {option.title}
                    </Typography>
                  </Box>
                </li>
              )}
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('selectProject')}
                  size="medium"
                  label={t('projectName')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      '& input': {
                        '&:-webkit-autofill': {
                          boxShadow: '0 0 0 1000px #292929 inset',
                          transition: 'background-color 5000s ease-in-out 0s',
                        },
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#5B5B5B',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#90CAF9',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      top: '-8px',
                    },
                    '& .MuiInputLabel-shrink': {
                      top: '0px',
                    },
                  }}
                />
              )}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'preventOverflow',
                      options: { boundary: 'window' },
                    },
                  ],
                },
                paper: {
                  sx: {
                    backgroundColor: theme.palette.background.disableTab,
                    fontSize: 16,
                    fontWeight: 400,
                    paddingY: '6px',
                  },
                },
              }}
            />
          </Box>
        </Box>
      }
      actionButtons={
        <Box width="100%" gap={2} display="flex" justifyContent="flex-end">
          <Button
            label={t('cancelButton')}
            onClick={onClose}
            variant="outlined"
            textTransform="uppercase"
          />
          <Button
            label={t('moveButton')}
            onClick={handleAddUser}
            variant="contained"
            disabled={!selectedProject || isMoving}
            loading={isMoving}
            textTransform="uppercase"
          />
        </Box>
      }
      paperStyles={{ width: '800px' }}
      triggerElement={undefined}
    />
  )
}

export default MoveImageDialog
